import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type PropsWithChildren,
} from 'react';

import {
  type AnalyticsClient,
  type AnalyticsConfig,
  type AnalyticsProvider as Provider,
  NoopAnalyticsClient,
  PostHogClient,
} from '@kushya/shared';

export interface AnalyticsProviderConfig extends AnalyticsConfig {
  provider?: Provider;
}

interface AnalyticsContextValue {
  client: AnalyticsClient;
  isReady: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function createClient(provider: Provider): AnalyticsClient {
  switch (provider) {
    case 'posthog':
      return new PostHogClient();
    case 'noop':
    default:
      return new NoopAnalyticsClient();
  }
}

export interface AnalyticsProviderProps extends PropsWithChildren {
  config: AnalyticsProviderConfig;
}

export function AnalyticsProvider({ config, children }: AnalyticsProviderProps) {
  const clientRef = useRef<AnalyticsClient | null>(null);

  if (!clientRef.current) {
    const shouldUseNoop = !isBrowser() || config.disabled;
    const provider = shouldUseNoop ? 'noop' : (config.provider ?? 'posthog');
    clientRef.current = createClient(provider);
  }

  const client = clientRef.current;

  useEffect(() => {
    if (!config.disabled) {
      client.init(config);
    }

    return () => {
      client.flush();
    };
  }, [client, config]);

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      client,
      isReady: client.isReady(),
    }),
    [client],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalyticsContext(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }

  return context;
}
